/* eslint-disable react/prop-types */
import React, {useState, useRef, useCallback} from 'react';
import {
  Button,
  CircularProgress,
  TextField,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {ArrowForward, ArrowBack, CancelOutlined} from '@mui/icons-material';
import InputMask from 'react-input-mask';

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://kops.apiireit.com/gateway';

// ─── Colors (shared) ─────────────────────────────────────────────────────────
const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  text: '#1a1a2e',
  muted: '#8a94a6',
  white: '#ffffff',
  success: '#059669',
  danger: '#e53935',
  warn: '#d97706',
};

// ─── CPF validation ───────────────────────────────────────────────────────────
function validateCPF(raw) {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(d[i]) * (10 - i);
  let r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== parseInt(d[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(d[i]) * (11 - i);
  r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === parseInt(d[10]);
}

// ─── Date validation dd/mm/yyyy ───────────────────────────────────────────────
function validateDate(str) {
  if (!str || str.replace(/\D/g, '').length < 8) return false;
  const [d, m, y] = str.split('/').map(Number);
  if (!d || !m || !y || y < 1900 || y > new Date().getFullYear()) return false;
  const dt = new Date(y, m - 1, d);
  return (
    dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
  );
}

function getAge(dob) {
  if (!validateDate(dob)) return 0;
  const [d, m, y] = dob.split('/').map(Number);
  const today = new Date();
  const birth = new Date(y, m - 1, d);
  let age = today.getFullYear() - birth.getFullYear();
  const mDiff = today.getMonth() - birth.getMonth();
  if (mDiff < 0 || (mDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

// ─── Photo requirements list ──────────────────────────────────────────────────
const PHOTO_REQS = [
  {ok: true, text: 'Fique de frente para a câmera'},
  {ok: true, text: 'Olhos e boca visíveis e desobstruídos'},
  {ok: true, text: 'Foto nítida, bem iluminada, sem reflexos'},
  {ok: false, text: 'Não fotografe outra foto ou tela'},
  {ok: false, text: 'Sem filtros, sem retoques, sem óculos escuros'},
  {ok: false, text: 'Não use fundo preto ou muito escuro'},
];

// ─── SVG person illustration (good vs bad) ────────────────────────────────────
const PersonIllustration = ({good}) => (
  <svg
    viewBox="0 0 120 150"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Background */}
    <rect
      width="120"
      height="150"
      rx="12"
      fill={good ? '#eef6ff' : '#fff5f5'}
      stroke={good ? '#06448e' : '#fca5a5'}
      strokeWidth="2"
    />
    {/* Face circle */}
    <circle
      cx="60"
      cy="52"
      r="28"
      fill={good ? '#fde8d8' : '#fde8d8'}
      stroke={good ? '#06448e22' : '#ef444422'}
      strokeWidth="1"
    />
    {/* Eyes */}
    {good ? (
      <>
        <ellipse cx="50" cy="48" rx="4" ry="5" fill="#1a1a2e" />
        <ellipse cx="70" cy="48" rx="4" ry="5" fill="#1a1a2e" />
        <circle cx="51.5" cy="46.5" r="1.5" fill="#fff" />
        <circle cx="71.5" cy="46.5" r="1.5" fill="#fff" />
      </>
    ) : (
      <>
        {/* Sunglasses */}
        <rect x="42" y="45" width="14" height="8" rx="4" fill="#1a1a2e" />
        <rect x="64" y="45" width="14" height="8" rx="4" fill="#1a1a2e" />
        <line
          x1="56"
          y1="49"
          x2="64"
          y2="49"
          stroke="#1a1a2e"
          strokeWidth="1.5"
        />
      </>
    )}
    {/* Nose */}
    <path
      d="M60 50 Q57 57 55 60 Q60 62 65 60 Q63 57 60 50"
      fill="none"
      stroke="#c4956a"
      strokeWidth="1.2"
    />
    {/* Mouth */}
    {good ? (
      <path
        d="M50 65 Q60 72 70 65"
        fill="none"
        stroke="#c4956a"
        strokeWidth="2"
        strokeLinecap="round"
      />
    ) : (
      <path
        d="M50 68 Q60 63 70 68"
        fill="none"
        stroke="#c4956a"
        strokeWidth="2"
        strokeLinecap="round"
      />
    )}
    {/* Ears */}
    <ellipse cx="32" cy="52" rx="5" ry="7" fill="#fde8d8" />
    <ellipse cx="88" cy="52" rx="5" ry="7" fill="#fde8d8" />
    {/* Hair */}
    {good ? (
      <path
        d="M32 42 Q40 18 60 20 Q80 18 88 42 Q80 30 60 32 Q40 30 32 42"
        fill="#3d2b1f"
      />
    ) : (
      <>
        <path
          d="M32 42 Q40 18 60 20 Q80 18 88 42 Q80 30 60 32 Q40 30 32 42"
          fill="#3d2b1f"
        />
        {/* Hat / filter overlay */}
        <rect
          x="28"
          y="18"
          width="64"
          height="22"
          rx="4"
          fill="#7c53e3"
          opacity="0.7"
        />
        <text
          x="60"
          y="33"
          textAnchor="middle"
          fontSize="10"
          fill="#fff"
          fontWeight="bold"
        >
          FILTRO
        </text>
      </>
    )}
    {/* Neck */}
    <rect x="53" y="78" width="14" height="16" rx="4" fill="#fde8d8" />
    {/* Shoulders */}
    <path
      d={
        good
          ? 'M20 130 Q20 100 38 96 Q48 92 60 92 Q72 92 82 96 Q100 100 100 130'
          : 'M20 130 Q20 100 38 96 Q48 92 60 92 Q72 92 82 96 Q100 100 100 130'
      }
      fill={good ? '#dbeafe' : '#ffe4e6'}
    />
    {/* Badge */}
    {good ? (
      <>
        <circle cx="98" cy="20" r="12" fill={C.success} />
        <text x="98" y="25" textAnchor="middle" fontSize="14" fill="#fff">
          ✓
        </text>
      </>
    ) : (
      <>
        <circle cx="98" cy="20" r="12" fill={C.danger} />
        <text x="98" y="25" textAnchor="middle" fontSize="14" fill="#fff">
          ✗
        </text>
      </>
    )}
    {/* Label */}
    <rect
      x="10"
      y="133"
      width="100"
      height="14"
      rx="4"
      fill={good ? C.success : C.danger}
      opacity="0.15"
    />
    <text
      x="60"
      y="143"
      textAnchor="middle"
      fontSize="10"
      fill={good ? C.success : C.danger}
      fontWeight="700"
    >
      {good ? 'FOTO CORRETA' : 'FOTO INCORRETA'}
    </text>
  </svg>
);

// ─── Upload area component ────────────────────────────────────────────────────
const UploadArea = ({file, existingUrl, onFile, onClear}) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setDrag(false);
      const f = e.dataTransfer.files?.[0];
      if (f && f.type.startsWith('image/')) onFile(f);
    },
    [onFile],
  );

  const handleChange = e => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
    e.target.value = '';
  };

  const preview = file ? URL.createObjectURL(file) : null;
  const hasPhoto = file || existingUrl;

  return (
    <div style={{position: 'relative'}}>
      {hasPhoto && (
        <IconButton
          onClick={onClear}
          size="small"
          style={{
            position: 'absolute',
            top: -10,
            right: -10,
            zIndex: 2,
            background: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
          }}
        >
          <CancelOutlined style={{fontSize: 18, color: C.danger}} />
        </IconButton>
      )}

      <div
        onDragOver={e => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          width: '100%',
          minHeight: hasPhoto ? 220 : 140,
          borderRadius: 16,
          border: hasPhoto ? 'none' : `2px dashed ${drag ? C.laranja : C.azul}`,
          background: hasPhoto
            ? 'transparent'
            : drag
            ? 'rgba(255,102,0,0.05)'
            : '#f4f6fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'all 0.2s',
          position: 'relative',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{display: 'none'}}
          onChange={handleChange}
        />
        {hasPhoto ? (
          <img
            src={preview || existingUrl}
            alt="preview"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 16,
            }}
            onLoad={() => {
              if (preview) URL.revokeObjectURL(preview);
            }}
          />
        ) : (
          <div style={{textAlign: 'center', padding: 24}}>
            <div style={{fontSize: 40, marginBottom: 8}}>📷</div>
            <p
              style={{margin: 0, fontSize: 14, fontWeight: 700, color: C.azul}}
            >
              Toque para tirar foto ou escolher da galeria
            </p>
            <p style={{margin: '4px 0 0', fontSize: 12, color: C.muted}}>
              JPG, PNG · máx. 10 MB
            </p>
          </div>
        )}
      </div>

      {/* Quality tips shown after upload */}
      {hasPhoto && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 10,
            background:
              existingUrl && !file
                ? 'rgba(5,150,105,0.05)'
                : 'rgba(6,68,142,0.05)',
            border: `1px solid ${existingUrl && !file ? C.success : C.azul}20`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{fontSize: 16}}>
            {existingUrl && !file ? '✅' : '🔍'}
          </span>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: existingUrl && !file ? C.success : C.azul,
              fontWeight: 600,
            }}
          >
            {existingUrl && !file
              ? 'Foto salva anteriormente. Toque na imagem para trocar.'
              : 'Verifique: rosto visível, fundo claro, sem filtros, foto nítida.'}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Gender card ──────────────────────────────────────────────────────────────
const GENEROS = [
  {id: 'M', label: 'Masculino', icon: '♂'},
  {id: 'F', label: 'Feminino', icon: '♀'},
  {id: 'NB', label: 'Não binário', icon: '⚧'},
  {id: 'NI', label: 'Prefiro não informar', icon: '—'},
];

// ══════════════════════════════════════════════════════════════════════════════
// STEP PESSOAL
// ══════════════════════════════════════════════════════════════════════════════
/**
 * Props
 * ─────
 * authUser        Firebase User
 * getToken        () => Promise<string>   — bearer token helper
 * sm              boolean
 * nome / setNome
 * cpf  / setCpf
 * dob  / setDob
 * gnro / setGnro
 * celular / setCelular
 * profileFile / setProfileFile   File | null
 * onNext          (dadosContaId: string, specificStep?: string) => void   — called after successful save, or with specificStep for CPF conflict
 * onBack          () => void
 */
const StepPessoal = ({
  authUser,
  getToken,
  sm,
  existingPhotoUrl,
  nome,
  setNome,
  cpf,
  setCpf,
  dob,
  setDob,
  gnro,
  setGnro,
  celular,
  setCelular,
  profileFile,
  setProfileFile,
  onNext,
  onBack,
}) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0); // 0-100, used for visual only
  const [phase, setPhase] = useState(''); // 'saving' | 'uploading'

  // ── derive step from driver data ────────────────────────────────────────
  const deriveStep = d => {
    // 1. Intenção / tipo de veículo
    if (!d.type) return 'intencao';

    // 2. Dados pessoais
    if (!d.nome || !d.Imagem || !d.cpf || !d.dob) return 'pessoal';

    // 3. CNH
    if (d.sttcnh === 0 && d.sttcnhup === 0 && !d.cnhup) return 'cnh';

    // 4. Veículo
    if ((!d.veiculo || !d.marca || !d.placa) && d.pix == '') return 'veiculo';

    // 5. Localização / ponto
    if (d.pontoStatusVinculo === 0) return 'localizacao';

    // 6. Conta bancária
    if (!d.Banco || !d.Conta || !d.Agencia || !d.TConta) return 'banco';

    // 7. Navegação
    if (!d.nav) return 'nav';

    // All done
    return 'done';
  };

  // ── validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    if (!profileFile && !existingPhotoUrl) return 'Adicione uma foto de rosto.';
    if (!nome.trim()) return 'Informe seu nome completo.';
    if (!celular || celular.replace(/\D/g, '').length < 11)
      return 'Informe um celular válido com DDD.';
    if (!validateCPF(cpf)) return 'CPF inválido. Verifique os dígitos.';
    if (!validateDate(dob)) return 'Data de nascimento inválida (DD/MM/AAAA).';
    if (getAge(dob) < 18) return 'É preciso ter 18 anos ou mais para dirigir.';
    if (getAge(dob) > 100) return 'Data de nascimento inválida.';
    if (!gnro) return 'Selecione o seu gênero.';
    return null;
  };

  // ── submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setLoading(true);

    try {
      const token = await getToken();
      const uid = authUser.uid;

      // 1. Save personal data ─────────────────────────────────────────────────
      setPhase('saving');
      const params = new URLSearchParams({
        userid: uid,
        nome: nome.trim(),
        nscm: dob, // DD/MM/AAAA  (server uses this field name)
        cpf: cpf,
        phone: celular,
      });

      const saveRes = await fetch(
        `${API_BASE}/parceiros/dadospessoais?${params}`,
        {
          method: 'PUT',
          headers: {Authorization: `Bearer ${token}`},
        },
      );

      if (!saveRes.ok) {
        const body = await saveRes.text();
        if (saveRes.status === 409 || body.includes('cpf')) {
          // CPF already exists — show message and jump to correct step
          setError('CPF já cadastrado. Continuando para o próximo passo...');
          try {
            const token = await getToken();
            const res = await fetch(
              `${API_BASE}/parceiros/get/user?userid=${authUser.uid}`,
              {headers: {Authorization: `Bearer ${token}`}},
            );
            if (res.ok) {
              const data = await res.json();
              const nextStep = deriveStep(data);
              setTimeout(() => onNext(null, nextStep), 1500); // Delay to show message
              return;
            }
          } catch (fetchErr) {
            console.warn(
              'Failed to fetch driver data after CPF conflict:',
              fetchErr,
            );
          }
          throw new Error('CPF já cadastrado em outra conta.');
        }
        throw new Error(`Erro ao salvar dados (${saveRes.status}).`);
      }

      const dadosConta = await saveRes.json(); // ParceirosDadosmodels
      const docId = dadosConta._id;

      // 2. Upload profile photo (skip if reusing existing photo) ────────────
      if (profileFile) {
        setPhase('uploading');
        setUploadPct(10);

        const form = new FormData();
        form.append('file', profileFile);

        const progressInterval = setInterval(() => {
          setUploadPct(prev => (prev < 85 ? prev + 8 : prev));
        }, 300);

        const uploadRes = await fetch(
          `${API_BASE}/s3/upload?userid=${uid}&docid=${docId}&svc=Foto&index=0`,
          {
            method: 'POST',
            headers: {Authorization: `Bearer ${token}`},
            body: form,
          },
        );

        clearInterval(progressInterval);
        setUploadPct(100);

        if (!uploadRes.ok) {
          console.warn('Photo upload failed, continuing anyway');
        }
      }

      // 3. Advance ────────────────────────────────────────────────────────────
      setTimeout(() => {
        onNext(docId);
      }, 400);
    } catch (e) {
      setError(e.message || 'Erro ao salvar. Tente novamente.');
      setLoading(false);
      setPhase('');
      setUploadPct(0);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  const cpfValid =
    cpf.replace(/\D/g, '').length === 11 ? validateCPF(cpf) : null;
  const dobValid =
    dob.replace(/\D/g, '').length === 8 ? validateDate(dob) : null;
  const ageOk = dobValid ? getAge(dob) >= 18 : null;

  return (
    <div>
      {/* ── Photo guide section ─────────────────────────────────────────────── */}
      <div style={{marginBottom: 24}}>
        <p
          style={{
            margin: '0 0 12px',
            fontSize: 15,
            fontWeight: 800,
            color: C.text,
          }}
        >
          📷 Foto de perfil
        </p>
        <p
          style={{
            margin: '0 0 14px',
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.55,
          }}
        >
          Sua foto é usada para identificação pelas passageiras/passageiros
          durante a corrida. Ela precisa ser nítida e mostrar seu rosto.
        </p>

        {/* Examples row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{height: sm ? 130 : 150}}>
              <PersonIllustration good />
            </div>
          </div>
          <div>
            <div style={{height: sm ? 130 : 150}}>
              <PersonIllustration good={false} />
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div
          style={{
            background: '#f8f9fb',
            borderRadius: 12,
            padding: '12px 14px',
            border: '1px solid #eef0f4',
            marginBottom: 16,
          }}
        >
          <p
            style={{
              margin: '0 0 8px',
              fontSize: 11,
              fontWeight: 700,
              color: C.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Requisitos da foto
          </p>
          {PHOTO_REQS.map((req, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 0',
                borderBottom:
                  i < PHOTO_REQS.length - 1 ? '1px solid #f0f2f5' : 'none',
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: req.ok ? `${C.success}18` : `${C.danger}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  flexShrink: 0,
                  color: req.ok ? C.success : C.danger,
                  fontWeight: 700,
                }}
              >
                {req.ok ? '✓' : '✗'}
              </span>
              <span style={{fontSize: 12, color: C.text}}>{req.text}</span>
            </div>
          ))}
        </div>

        {/* Upload area */}
        <UploadArea
          file={profileFile}
          existingUrl={!profileFile ? existingPhotoUrl : null}
          onFile={setProfileFile}
          onClear={() => setProfileFile(null)}
        />
      </div>

      {/* Divider */}
      <div style={{height: 1, background: '#f0f2f5', margin: '4px 0 24px'}} />

      {/* ── Personal fields ─────────────────────────────────────────────────── */}
      <p
        style={{
          margin: '0 0 16px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        📋 Seus dados
      </p>

      {/* Nome */}
      <TextField
        label="Nome completo *"
        variant="outlined"
        size="small"
        fullWidth
        style={{marginBottom: 16}}
        value={nome}
        onChange={e => setNome(e.target.value)}
        placeholder="Como consta no seu documento"
        disabled={loading}
        autoComplete="name"
      />

      {/* Celular */}
      <InputMask
        mask="(99) 99999-9999"
        value={celular}
        onChange={e => setCelular(e.target.value)}
        disabled={loading}
      >
        {() => (
          <TextField
            label="Celular / WhatsApp *"
            variant="outlined"
            size="small"
            fullWidth
            style={{marginBottom: 16}}
            placeholder="(71) 99999-9999"
            inputProps={{inputMode: 'numeric'}}
          />
        )}
      </InputMask>

      {/* CPF */}
      <InputMask
        mask="999.999.999-99"
        value={cpf}
        onChange={e => setCpf(e.target.value)}
        disabled={loading}
      >
        {() => (
          <TextField
            label="CPF *"
            variant="outlined"
            size="small"
            fullWidth
            style={{marginBottom: 4}}
            placeholder="000.000.000-00"
            inputProps={{inputMode: 'numeric'}}
            error={cpfValid === false}
            helperText={
              cpfValid === false
                ? 'CPF inválido — verifique os dígitos.'
                : cpfValid === true
                ? '✓ CPF válido'
                : ''
            }
            FormHelperTextProps={{
              style: {
                color: cpfValid === true ? C.success : undefined,
                fontWeight: cpfValid === true ? 600 : 400,
              },
            }}
          />
        )}
      </InputMask>

      {/* DOB */}
      <div style={{marginTop: 12}}>
        <InputMask
          mask="99/99/9999"
          value={dob}
          onChange={e => setDob(e.target.value)}
          disabled={loading}
        >
          {() => (
            <TextField
              label="Data de nascimento *"
              variant="outlined"
              size="small"
              fullWidth
              placeholder="DD/MM/AAAA"
              inputProps={{inputMode: 'numeric'}}
              error={dobValid === false || ageOk === false}
              helperText={
                dobValid === false
                  ? 'Data inválida.'
                  : ageOk === false
                  ? 'Você precisa ter 18 anos ou mais.'
                  : ageOk === true
                  ? `✓ ${getAge(dob)} anos`
                  : ''
              }
              FormHelperTextProps={{
                style: {
                  color: ageOk === true ? C.success : undefined,
                  fontWeight: ageOk === true ? 600 : 400,
                },
              }}
            />
          )}
        </InputMask>
      </div>

      {/* Gênero */}
      <div style={{marginTop: 16, marginBottom: 4}}>
        <p
          style={{
            margin: '0 0 10px',
            fontSize: 13,
            color: C.text,
            fontWeight: 600,
          }}
        >
          Gênero *
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: sm ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: 8,
          }}
        >
          {GENEROS.map(g => {
            const sel = gnro === g.id;
            return (
              <button
                key={g.id}
                onClick={() => setGnro(g.id)}
                disabled={loading}
                style={{
                  padding: '10px 6px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  border: `2px solid ${sel ? C.azul : '#e0e5ef'}`,
                  background: sel ? `${C.azul}0d` : '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  transition: 'all 0.15s',
                  fontFamily: "'Outfit','Segoe UI',sans-serif",
                }}
              >
                <span style={{fontSize: 18}}>{g.icon}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: sel ? 700 : 400,
                    color: sel ? C.azul : C.muted,
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {g.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 10,
            background: `${C.danger}0d`,
            border: `1px solid ${C.danger}30`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{fontSize: 16}}>⚠️</span>
          <p
            style={{margin: 0, fontSize: 13, color: C.danger, fontWeight: 600}}
          >
            {error == 'The string did not match the expected pattern.'
              ? 'CPF Já cadastrado! Entre em contato com nosso suporte pelo email suporte@ireit.com.br'
              : 'Erro ao tentar cadastrar'}
          </p>
        </div>
      )}

      {/* Upload progress */}
      {loading && (
        <div style={{marginTop: 14}}>
          <p
            style={{
              margin: '0 0 6px',
              fontSize: 12,
              color: C.muted,
              fontWeight: 600,
            }}
          >
            {phase === 'saving' && '⏳ Salvando seus dados…'}
            {phase === 'uploading' && `📤 Enviando foto… ${uploadPct}%`}
          </p>
          <LinearProgress
            variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
            value={uploadPct}
            sx={{
              borderRadius: 4,
              height: 6,
              backgroundColor: '#f0f2f5',
              '& .MuiLinearProgress-bar': {backgroundColor: C.laranja},
            }}
          />
        </div>
      )}

      {/* Nav */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 28,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          onClick={onBack}
          startIcon={<ArrowBack />}
          disabled={loading}
          style={{textTransform: 'none', color: C.muted, fontSize: 14}}
        >
          Voltar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          endIcon={loading ? null : <ArrowForward />}
          sx={{
            bgcolor: C.laranja,
            color: '#fff',
            borderRadius: '12px',
            height: 48,
            px: 3,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            ':hover': {bgcolor: C.laranjaD},
            ':disabled': {bgcolor: '#e0e0e0', color: '#aaa'},
          }}
        >
          {loading ? (
            <CircularProgress size={20} style={{color: '#fff'}} />
          ) : (
            'Salvar e continuar'
          )}
        </Button>
      </div>
    </div>
  );
};

export default StepPessoal;
